import React, {ReactNode, useRef, KeyboardEvent, RefObject} from 'react';
import {useIsomorphicLayoutEffect} from '@sail/engine';
import {useFocusManager, FocusScope} from '@sail/react-aria';

interface FocusOnMountProps {
  /**
   * Render prop function
   */
  children: (props: {
    ref: RefObject<HTMLElement>;
    onKeyDown: (event: KeyboardEvent) => void;
  }) => ReactNode;

  /**
   * Custom focus function to be called after the component mounts
   * @returns {boolean} True if focus was successfully set, false otherwise
   */
  onFocusMount: () => boolean;
}

export const FocusOnMount = ({children, onFocusMount}: FocusOnMountProps) => {
  const containerRef = useRef<HTMLElement>(null);
  const focusManager = useFocusManager();

  useIsomorphicLayoutEffect(() => {
    if (containerRef.current) {
      requestAnimationFrame(() => {
        const focusSucceeded = onFocusMount();
        if (!focusSucceeded) {
          focusManager.focusFirst();
        }
      });
    }
  }, []);

  const handleKeyDown = (event: KeyboardEvent) => {
    switch (event.key) {
      case 'ArrowRight':
        focusManager.focusNext({wrap: true});
        break;
      case 'ArrowLeft':
        focusManager.focusPrevious({wrap: true});
        break;
      default:
    }
  };

  return (
    <FocusScope contain>
      {children({
        ref: containerRef,
        onKeyDown: handleKeyDown,
      })}
    </FocusScope>
  );
};
